import { Editor, Element, Path, Text, Transforms } from 'slate';

import { resolveTableRowEntryByAnchorId } from './resolve-location-anchor';
import { stampReasoningOnFirstSourcedEdit } from './suggestion-reasoning';
import { htmlToAline } from '../../../serializers/html';
import type {
  AlineDescendant,
  InsertEdit,
  InsertTableRowEdit,
  TableCellElement,
  TableElement,
  TableRowElement,
} from '../../internal';
import {
  AlineTransforms,
  createDefaultTableCellElement,
  isTableElement,
  Queries,
} from '../../internal';
import type { SuggestionSource } from '../../types';

export interface ApplyInsertRowsOutputOptions {
  /**
   * The author ID for the suggested edits (typically the AI or system user)
   */
  authorId: string;
  /**
   * Identifies the whole row insertion as one group, so accept/reject acts
   * on it as a unit
   */
  groupId: string;
  /**
   * One or more `<tr>` elements (already stripped of any echoed markers)
   */
  html: string;
  /**
   * The reference row's `row-…` anchor id, as emitted in the LLM-facing
   * document format.
   */
  locationAnchor: string;
  /**
   * Whether the rows go immediately above or below the reference row
   */
  position: 'above' | 'below';
  /**
   * Why the rows are being inserted — stored on the first sourced edit of the
   * inserted rows
   */
  reasoning: string;
  /**
   * The source stamp carried by every inserted mark — or undefined to
   * materialize the rows as the author's own tracked changes, with no source
   * stamps and no stored reasoning (no AI chrome).
   */
  source: SuggestionSource | undefined;
}

/**
 * Applies an `insert-rows` structured edit output to the editor as suggested
 * new rows in an existing table — the AI-sourced equivalent of the "Insert
 * row above/below" command in suggesting mode, with content.
 *
 * The flow:
 * 1. Resolves the `row-…` anchor id to the reference row (throws when the
 *    anchor is not a row id or matches no row).
 * 2. Parses the `<tr>` html — wrapped in a `<table>` first, since bare row
 *    fragments don't survive HTML parsing — registers any list definitions it
 *    minted, and grafts the parsed rows onto the target table's geometry:
 *    cell widths come from the reference row where it has a single-column
 *    cell at that column, short rows are padded with empty cells, and a row
 *    with more cells than the table has columns throws (silent truncation
 *    would lose content).
 * 3. Stamps every grafted row with a sourced `insertTableRow` edit and every
 *    text node inside with a sourced `insertNode` edit, so the insertion
 *    renders as suggested content and rejecting removes the rows wholesale.
 * 4. Inserts the rows above/below the reference row and stores the reasoning
 *    on the first sourced edit. Block suggestions carry no suggestion-marker
 *    pair — the sourced edits themselves are the suggestion.
 */
export function applyInsertRowsOutput(editor: Editor, options: ApplyInsertRowsOutputOptions): void {
  const { authorId, groupId, html, locationAnchor, position, reasoning, source } = options;

  const [referenceRow, referenceRowPath] = resolveTableRowEntryByAnchorId(editor, locationAnchor);
  const tableEntry = Editor.above<TableElement>(editor, {
    at: referenceRowPath,
    match: isTableElement,
  });
  if (!tableEntry) {
    throw new Error(`Table row with anchor id '${locationAnchor}' is not inside a table`);
  }
  const [table] = tableEntry;

  /**
   * Bare `<tr>` fragments are dropped by HTML parsers outside a `<table>`
   * context, so wrap before parsing and lift the rows back out.
   */
  const { blocks, listDefinitions } = htmlToAline(editor, {
    at: Path.parent(Path.parent(referenceRowPath)),
    html: `<table>${html}</table>`,
  });
  const parsedRows = blocks.filter(isTableElement).flatMap(parsedTable => parsedTable.children);
  if (parsedRows.length === 0) {
    throw new Error(
      `Insert-rows html for anchor '${locationAnchor}' contained no table rows — it must consist of <tr> elements`
    );
  }

  /**
   * Register the list definitions `htmlToAline` minted for list paragraphs
   * inside the inserted cells — a referenced listId with no definition renders
   * no marker and loses its list formatting on export.
   */
  for (const [listDefinitionId, listDefinition] of Object.entries(listDefinitions)) {
    AlineTransforms.createListDefinition(editor, { listDefinition, listDefinitionId });
  }

  const timestamps = { createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
  const rowEdit: InsertTableRowEdit = {
    authorId,
    ...timestamps,
    groupId,
    source,
    type: 'insertTableRow',
    value: {},
  };
  const textEdit: InsertEdit = { authorId, ...timestamps, source, type: 'insertNode', value: {} };

  /**
   * Column geometry comes from the whole table, not the reference row's cell
   * count — a reference row with merged (columnSpan > 1) cells has fewer
   * cells than the table has columns.
   */
  const columnCount = Queries.getTableColumnCount(table);
  const referenceWidths = getRowWidthsByColumnIndex(referenceRow);
  const newRows = parsedRows.map((parsedRow): TableRowElement => {
    if (parsedRow.children.length > columnCount) {
      throw new Error(
        `Insert-rows html has a row with ${parsedRow.children.length} cells but the table has ${columnCount} columns`
      );
    }
    /**
     * Content comes from the parsed html; geometry comes from the table —
     * each cell inherits the reference row's width at its column index where
     * the reference row has a single-column cell there, and short rows are
     * padded with empty cells.
     */
    const cells = Array.from({ length: columnCount }, (_, columnIndex) => {
      const referenceWidth = referenceWidths.get(columnIndex);
      const parsedCell = parsedRow.children[columnIndex];
      if (!parsedCell) return createDefaultTableCellElement({ width: referenceWidth });
      return { ...parsedCell, width: referenceWidth };
    });
    const newRow: TableRowElement = {
      ...parsedRow,
      children: cells,
      suggestedEdits: [rowEdit],
    };
    stampTextNodesAsInserted(newRow, textEdit);
    return newRow;
  });

  const insertionPath = position === 'above' ? referenceRowPath : Path.next(referenceRowPath);
  Transforms.insertNodes(editor, newRows, { at: insertionPath });

  const firstRowPath = insertionPath;
  const lastRowPath = [
    ...insertionPath.slice(0, -1),
    Queries.indexInParent(insertionPath) + newRows.length - 1,
  ];

  // A sourceless insertion has no sourced edit to stamp — and no AI reasoning
  // belongs on the author's own tracked changes.
  if (!source) return;

  stampReasoningOnFirstSourcedEdit(editor, {
    at: Editor.range(editor, firstRowPath, lastRowPath),
    groupId,
    reasoning,
  });
}

/**
 * Recursively stamps every text node under the element with the sourced
 * `insertNode` edit, mutating in place. The nodes are plain objects not yet
 * inserted into the editor, so direct mutation is safe.
 */
function stampTextNodesAsInserted(element: { children: AlineDescendant[] }, edit: InsertEdit) {
  for (const child of element.children) {
    if (Text.isText(child)) {
      child.suggestedEdits = [...(child.suggestedEdits ?? []), edit];
      continue;
    }
    if (Element.isElement(child)) stampTextNodesAsInserted(child, edit);
  }
}

/**
 * The reference row's cell width at each column index it has a single-column
 * cell at. A merged cell's width covers its whole span rather than any one
 * column, so the columns it covers inherit no width — nor do columns past the
 * end of a short row.
 */
function getRowWidthsByColumnIndex(row: TableRowElement) {
  const widths = new Map<number, TableCellElement['width']>();
  let columnIndex = 0;
  for (const cell of row.children) {
    const columnSpan = Queries.renderedTableCellColumnSpan(cell);
    if (columnSpan === 1) widths.set(columnIndex, cell.width);
    columnIndex += columnSpan;
  }
  return widths;
}
